import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { soundApi } from "../soundDetail/soundApi";
import useSoundViewReport from "./useSoundViewReport";

// A post video. When the post was made with a catalog sound (post.sound_id) the video plays with that sound: the clip's
// own audio is muted and the sound plays in step with it (play, pause, seek, end), and a "Sound" link under the video
// opens the sound's page. If the sound cannot be loaded the video simply keeps its own audio.
// `silenced` is set when the rights team muted the video (post.rights_enforcement === "mute"): the video still shows, but it
// has no audio at all and the viewer cannot switch it back on.
// `postId` lets the player tell the server a video made with a sound was watched (soundApi.videoView): once the viewer has actually played
// 5 seconds of it, the artist earns royalties for the view (see useSoundViewReport; the server decides whether it counts).
// `licenceNeeded` is set on the owner's own video when their account is a monetising creator and the sound has no covering licence
// (post.licence_needed): a "Licence needed" note with a link to the sound page. It is a notice only: the video stays live and audible.
const soundCache = new Map();
export const loadSound = (id) => {
  if (!soundCache.has(id)) {
    soundCache.set(id, soundApi.detail(id).catch((e) => { soundCache.delete(id); throw e; }));
  }
  return soundCache.get(id);
};

export default function PostVideo({ src, soundId, postId, silenced = false, licenceNeeded = false, className = "", width = "100%", height }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [sound, setSound] = useState(null);
  const [soundOff, setSoundOff] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSound(null);
    if (!soundId || silenced) return undefined;
    loadSound(soundId).then((s) => { if (!cancelled && s?.audio_url) setSound(s); }).catch(() => { /* keep the video's own audio */ });
    return () => { cancelled = true; };
  }, [soundId, silenced]);

  // Keep the sound in step with the video.
  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video || !audio || !sound) return undefined;
    video.muted = true;
    const align = () => {
      const length = audio.duration;
      audio.currentTime = Number.isFinite(length) && length > 0 ? video.currentTime % length : video.currentTime;
    };
    const onPlay = () => { align(); audio.play().catch(() => { /* the browser blocked it: the video stays silent until played again */ }); };
    const onPause = () => audio.pause();
    const onSeeked = () => { align(); };
    const onEnded = () => { audio.pause(); };
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("ended", onEnded);
    if (!video.paused) onPlay();
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("ended", onEnded);
      audio.pause();
    };
  }, [sound]);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = soundOff; }, [soundOff, sound]);

  // The artist earns royalties for a video made with their sound: report the view once it has really been played (see the hook).
  useSoundViewReport(videoRef, { postId, enabled: !!soundId && !silenced, key: src });

  // A muted video stays muted even if the viewer drags the volume up.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !silenced) return undefined;
    const keepMuted = () => { video.muted = true; };
    keepMuted();
    video.addEventListener("volumechange", keepMuted);
    return () => video.removeEventListener("volumechange", keepMuted);
  }, [silenced, src]);

  const artist = sound?.artists?.length ? sound.artists.map((a) => a.name).join(", ") : sound?.artist_name;

  return (
    <div>
      <video ref={videoRef} src={src} controls playsInline width={width} height={height} className={className} />
      {licenceNeeded && soundId && (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="post-licence-needed">
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">Licence needed</span>
          <span className="text-gray-600">This sound needs a licence for your account.</span>
          <Link to={`/sound/${soundId}`} state={{ from: "post" }} className="font-semibold text-gray-800 underline">Get a licence</Link>
        </p>
      )}
      {silenced && <p className="mt-2 text-xs text-gray-500" data-testid="post-silenced">The audio of this video was turned off because of a rights claim on its sound.</p>}
      {sound && (
        <>
          <audio ref={audioRef} src={sound.audio_url} preload="auto" />
          <div className="mt-2 flex items-center gap-3 text-sm" data-testid="post-sound">
            <Link to={`/sound/${sound.id}`} state={{ from: "post" }} className="min-w-0 truncate font-semibold text-gray-800 hover:underline">
              ♪ {sound.title}{artist ? ` · ${artist}` : ""}
            </Link>
            <button type="button" onClick={() => setSoundOff((v) => !v)} className="shrink-0 text-xs font-semibold text-gray-600 underline" aria-pressed={soundOff}>
              {soundOff ? "Unmute sound" : "Mute sound"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
